/**
 * Step3Success — confirmation screen for the unified <LeadForm>.
 *
 * Structure adapted from the legacy broker modal: editorial
 * confirmation pill, optional property reminder, body copy, 01/02/03
 * Fraunces italic numbered list, and a soft CTA.
 *
 * The CTA copy / behavior depends on `postSubmit`:
 *   - 'thank-you'             → "Continuar explorando" → calls onClose
 *   - 'create-account-prompt' → "Crear cuenta con tu WhatsApp" → calls
 *     onClose for Unit 4 (account creation flow is deferred; the variant
 *     copy already signals the intent).
 *   - 'redirect-wa'           → "Continuar explorando" → calls onClose
 *     (the WA redirect itself was issued by the submit handler).
 */

import CheckRounded from '@mui/icons-material/CheckRounded'
import { useTranslation } from 'next-i18next'

import SelfReportedSourcePrompt from 'src/components/SelfReportedSource'
import type { SelfReportedAudience } from 'src/components/SelfReportedSource/options'
import { useAuthProvider } from 'src/context/AuthProvider'
import type {
  LeadFormConfig,
  ListingVariantMeta
} from 'src/lib/listingVariants/types'
import {
  SelfReportedSourceSurface,
  type IConfiguration,
  type ILocation
} from 'src/types/graphql'
import { numberRounedUpWithoutDecimals } from 'src/utils'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

type Step3SuccessProps = {
  leadForm: LeadFormConfig
  meta: Pick<ListingVariantMeta, 'dealType' | 'locationType'>
  location?: ILocation | null
  configuration?: IConfiguration | null
  onClose?: () => void
  /** Who just converted — picks the ¿Cómo nos conociste? option set. */
  audience: SelfReportedAudience
  /** The contact just submitted — identifies an anonymous answer in HubSpot. */
  contact: { email?: string | null; phone?: string | null }
}

/**
 * Step-3 copy sets, selected by `leadForm.leadCopyKey` — the variant slice,
 * never an inline `dealType` branch (the listing-variant rule). The same
 * discriminant picks Step 2's header subtitle (STEP2_HEADER_SUBTITLE in
 * `Step2Contact.tsx`), so the two screens cannot promise different things.
 *
 * `default` keeps the shipped "un asesor de SpotMe te contactará" promise,
 * which is true on Broker / Fleet / Monthly.
 *
 * `traditional` is for the surfaces where SpotMe CONNECTS the renter to an
 * operator it neither manages nor observes, so it makes **no response-time
 * promise on the operator's behalf** (spec §3) and offers recourse instead.
 *
 * ⚠️ **The `traditional` set must be true on BOTH host-lead disclosure tiers,
 * and it therefore NAMES NOBODY as the party who makes contact.** This screen
 * is picked by the listing VARIANT, but who actually handles the lead is
 * decided server-side by `resolveHostLeadDisclosure` + the presence of a
 * partner TEAM (`NotificationService.dispatchHostLead`). A Traditional
 * listing owned by an individual host (`location.submittedBy`), or by a team
 * whose `isPartner` is unset, resolves to the **nudge** tier: the host is
 * pinged WITHOUT the renter's name, phone or email, so they literally cannot
 * make contact — a SpotMe asesor works that lead off the admin email instead.
 * Copy that says "el operador del espacio te contacta" is false for every one
 * of those renters. Wording that does not name the party ("te van a
 * contactar") is true on both. Do not "improve" it by naming the operator,
 * and do not plumb the disclosure tier to the client to branch on it.
 *
 * For the same reason the "más adelante te preguntamos si ya quedaste"
 * promise is NOT here: the verification ladder only enrols full-disclosure
 * leads (`buildInitialVerification` returns undefined for nudge), so on the
 * nudge tier we never ask. That line lives in the L-N01 email, which IS
 * gated on `disclosure === 'full'`. Nothing legal is lost — the follow-up
 * CONSENT disclosure is in `LEAD_FORM.STEP2.PRIVACY`, which renders
 * unconditionally on every variant on the step before submit.
 */
const STEP3_COPY = {
  default: {
    body: 'LEAD_FORM.STEP3.BODY',
    steps: [
      'LEAD_FORM.STEP3.STEP_01',
      'LEAD_FORM.STEP3.STEP_02',
      'LEAD_FORM.STEP3.STEP_03'
    ],
    recourseNote: null
  },
  traditional: {
    body: 'LEAD_FORM.STEP3.BODY_TRADITIONAL',
    steps: [
      'LEAD_FORM.STEP3.STEP_01_TRADITIONAL',
      'LEAD_FORM.STEP3.STEP_02_TRADITIONAL',
      'LEAD_FORM.STEP3.STEP_03_TRADITIONAL'
    ],
    recourseNote: 'LEAD_FORM.STEP3.RECOURSE_NOTE'
  }
} as const

export default function Step3Success({
  leadForm,
  location,
  configuration,
  onClose,
  audience,
  contact
}: Step3SuccessProps) {
  const { t } = useTranslation('details')
  const { currentUser } = useAuthProvider()

  const showAccountPrompt =
    leadForm.postSubmit === 'create-account-prompt' && !currentUser

  const ctaKey = showAccountPrompt
    ? 'LEAD_FORM.STEP3.CTA_CREATE_ACCOUNT'
    : 'LEAD_FORM.STEP3.CTA_CONTINUE'

  const price = configuration?.price ?? null
  const showPropertyReminder = Boolean(location && configuration && price)

  const copy = STEP3_COPY[leadForm.leadCopyKey]

  return (
    <div className="flex flex-col py-2 md:py-3">
      {/* Editorial confirmation pill */}
      <div className="mb-6 inline-flex items-center gap-2.5 self-start border border-blue-500 bg-cream-300 px-3 py-1.5">
        <CheckRounded className="!text-[16px] text-blue-500" />
        <span className="font-display text-[10px] tracking-[0.16em] text-blue-500 uppercase md:text-[11px]">
          {t('LEAD_FORM.STEP3.PILL')}
        </span>
      </div>

      {/* Optional property reminder (price + locationType label) */}
      {showPropertyReminder && (
        <div className="border-t border-ink-100/15 pt-4">
          <p className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
            {t('LEAD_FORM.STEP3.PROPERTY_EYEBROW')}
          </p>
          <p className="mt-1 font-display text-[22px] leading-none font-medium text-ink-700 md:text-[26px]">
            <span className="italic">
              ${numberRounedUpWithoutDecimals(price ?? 0)}
            </span>
            <span className="ml-1 text-[14px] text-ink-200 md:text-[15px]">
              {t('LEAD_FORM.STEP3.PER_MONTH')}
            </span>
          </p>
        </div>
      )}

      {/* Body copy */}
      <p className="mt-6 max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
        {t(copy.body)}
      </p>

      {/* "What happens next" — Fraunces italic numerals */}
      <ol className="mt-8 grid grid-cols-1 gap-5 border-t border-ink-100/15 pt-6">
        {copy.steps.map((key, idx) => (
          <li key={key} className="flex items-baseline gap-4">
            <span className="font-display text-[28px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic md:text-[32px]">
              0{idx + 1}
            </span>
            <span className="text-[14px] leading-relaxed text-ink-700 md:text-[15px]">
              {t(key)}
            </span>
          </li>
        ))}
      </ol>

      {/* Recourse note — Traditional / on-demand only. SpotMe hands these
          leads to a party it neither manages nor observes, so it cannot make a
          response-time promise on anyone's behalf (spec §3). What it CAN
          offer, and what this line is, is a way back to SpotMe. Keep it free
          of any time window and of any named counterparty — see STEP3_COPY.

          The link makes it ACTIONABLE: the sentence tells the renter to write
          to us, and on the email-submit path nothing else on this screen gives
          them a way to. It routes to the corporate number via
          `buildWhatsappUrl` (never a specialist's phone — root CLAUDE.md), and
          reuses the REVISIT prefill so LangGraph sees one consistent
          "ya envié una solicitud y quiero darle seguimiento" opener from both
          follow-up surfaces. */}
      {copy.recourseNote && (
        <p className="mt-6 max-w-md border-t border-ink-100/15 pt-5 text-[13px] leading-relaxed text-ink-200 md:text-[14px]">
          {`${t(copy.recourseNote)} `}
          <a
            href={buildWhatsappUrl(t('LEAD_FORM.REVISIT.WHATSAPP_MESSAGE'))}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-500 underline underline-offset-2 transition-colors hover:text-ink-700"
          >
            {t('LEAD_FORM.STEP3.RECOURSE_WA_CTA')}
          </a>
        </p>
      )}

      {/* ¿Cómo nos conociste? — AFTER the lead is saved, so it can never
          cost this conversion. One tap, optional, asked once per person. */}
      <SelfReportedSourcePrompt
        surface={SelfReportedSourceSurface.LeadForm}
        audience={audience}
        contact={contact}
        className="mt-6"
      />

      {/* Soft CTA */}
      <button
        type="button"
        onClick={onClose}
        className="mt-8 inline-flex w-full items-center justify-center border border-ink-700 bg-ink-700 px-6 py-3.5 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:border-blue-500 hover:bg-blue-500"
      >
        {t(ctaKey)}
      </button>
    </div>
  )
}
